import type {
    EventSeriesData,
    NextLocale,
    VenueId,
} from '@beat-venues/common-types';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { twMerge } from 'tailwind-merge';

import {
    Button,
    Heading,
    Paragraph,
    StatusLabel,
    Text,
    TextLink,
} from '../../';
import type { CommonProps } from '../../types';
import { getEventDetailLink } from '../../utils/domain-util';

export interface EventCardProps
    extends CommonProps,
        VariantProps<typeof EventCardTextStyles>,
        Pick<
            EventSeriesData,
            | 'name'
            | 'tourName'
            | 'date'
            | 'datePrefix'
            | 'statusLabel'
            | 'slug'
            | 'image'
            | 'venueName'
        > {
    venueId: VenueId;
    basePath: string;
    orderId?: string;
    eventId?: string;
    isVip?: boolean;
    isPast?: boolean;
    buttonLabel?: string;
}

export const EventCardStyles = cva(
    'relative click-area group overflow-hidden rounded-lg w-fit'
);

export const EventCardTextStyles = cva(
    'absolute bottom-0 left-0 w-full h-full flex flex-col justify-end py-3 px-4',
    {
        variants: {
            size: {
                sm: 'md:p-6',
                lg: 'md:px-8 lg:px-12 md:py-6',
            },
        },
        defaultVariants: { size: 'sm' },
    }
);

export const EventCardImageStyles = cva('', {
    variants: {
        color: {
            past: 'grayscale group-hover:grayscale-0 opacity-50 transition group-hover:scale-105 group-hover:opacity-100',
            normal: 'fade-dark transition group-hover:scale-105',
            vip: 'bg-gradient-myBeat aspect-banner relative box-border rounded-lg object-cover p-2',
        },
    },
});

export const EventCardLabelStyles = cva('md:mb-2 self-start', {
    variants: {
        size: {
            sm: 'hidden md:inline-block',
            lg: 'inline-block',
        },
    },
    defaultVariants: { size: 'sm' },
});

export const EventCard = ({
    className,
    image,
    name,
    tourName,
    date,
    size,
    slug,
    eventId,
    venueName,
    venueId,
    basePath,
    orderId,
    datePrefix,
    statusLabel,
    isVip,
    isPast,
    buttonLabel,
    testId = 'event-card',
    ...props
}: EventCardProps): JSX.Element => {
    const { t, i18n } = useTranslation();

    const getHref = () => {
        if (slug && isPast) {
            return getEventDetailLink(
                venueId,
                slug,
                i18n.language as NextLocale
            );
        }
        if (slug && !isPast) {
            return slug;
        }
        return `${basePath}/${orderId}-${eventId}`;
    };

    const color = isPast ? 'past' : isVip ? 'vip' : 'normal';

    return (
        <div
            className={twMerge(EventCardStyles({ className }))}
            {...props}
            data-testid={testId}
        >
            {isVip && !isPast && (
                <div className="bg-gradient-vip absolute right-14 top-0 z-10 w-20 rounded-b-lg p-2 pt-4 shadow-[4px_3px_4px_rgba(0,0,0,0.5)]">
                    <img src="../assets/vip/logo/logo.svg" alt="premium logo" />
                </div>
            )}
            <div className={twMerge(EventCardImageStyles({ color }))}>
                <div
                    className={isVip ? 'h-full overflow-hidden rounded-md' : ''}
                >
                    <img
                        className={`${
                            isVip && !isPast
                                ? 'transition group-hover:scale-105'
                                : 'aspect-banner object-cover'
                        }`}
                        alt={image?.alt || 'placeholder'}
                        src={
                            image?.url ||
                            '/assets/my-beat/fallback/fallback.jpg'
                        }
                        width="823"
                        height="462"
                    />
                </div>
            </div>
            <div className={twMerge(EventCardTextStyles({ size }))}>
                {statusLabel && (
                    <StatusLabel
                        className={twMerge(EventCardLabelStyles({ size }))}
                    >
                        {statusLabel}
                    </StatusLabel>
                )}

                <Heading
                    as="h2"
                    className="hover-bg-white custom-text-shadow mb-0 line-clamp-1 drop-shadow lg:mb-1.5"
                    size={size === 'lg' ? 'display' : 'h4'}
                >
                    {name}
                </Heading>
                {slug && (
                    <TextLink
                        className="click-area-link hover:no-underline"
                        href={getHref()}
                        underline={false}
                    >
                        <Text className="sr-only">{name}</Text>
                    </TextLink>
                )}

                {tourName && size === 'lg' && (
                    <Heading
                        as="h3"
                        className="custom-text-shadow mb-1 text-sm lg:mb-3"
                        size="h4"
                    >
                        {tourName}
                    </Heading>
                )}
                {date && (
                    <Paragraph className="custom-text-shadow lg:text-lg lg:font-normal">
                        {datePrefix ? t('date.from', { date: date }) : date}
                        {venueId && ` • ${venueName}`}
                    </Paragraph>
                )}
                {buttonLabel && (
                    <Button
                        className="mb-2"
                        intent={isVip ? 'vip' : 'secondary'}
                        fullWidth={false}
                        size="sm"
                    >
                        {buttonLabel}
                    </Button>
                )}
            </div>
        </div>
    );
};
